"use client";

import { imageApiUrl } from "@/lib/images/urls";

import Link from "next/link";
import Image from "next/image";
import { useMessages } from "next-intl";
import { useEffect, useMemo, useState } from "react";

import type { SiteCopy, SiteLanguage } from "@/lib/site-data";
import { withLanguagePrefix } from "@/lib/site-locale";
import { formatDateTime } from "@/lib/site-date";
import type { ClusterDetailRecord } from "@/lib/site-queries";
import { BandBadge } from "@/components/site/band-badge";
import { ClusterCarousel } from "@/components/site/cluster-carousel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

function pickTranslation(translations: Array<{ language: string; title: string; summary: string }>, language: SiteLanguage) {
  return translations.find((item) => item.language === language) ?? translations[0] ?? null;
}

export function ClusterDetailView({ cluster, language }: { cluster: ClusterDetailRecord; language: SiteLanguage }) {
  // `language` is authoritative from the URL — see home-view.tsx for the
  // full hydration-mismatch rationale. We no longer read it from
  // usePreferences(), which was emitting /en/artists/... on /tr pages
  // because preferences hydrate from localStorage after SSR.
  const copy = useMessages() as SiteCopy;
  const translation = pickTranslation(cluster.translations, language);
  const [showAllTranslations, setShowAllTranslations] = useState(false);
  // Embla (inside ClusterCarousel) mutates the DOM on first mount; render
  // a static first-image placeholder on SSR + until hydration lands, then
  // swap in the real swipeable carousel. The LCP image still ships in the
  // initial HTML so SEO/Core Web Vitals are unaffected.
  const [mounted, setMounted] = useState(false);
  const [failedArticleImageIds, setFailedArticleImageIds] = useState<Set<string>>(new Set());
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setMounted(true), []);

  const visibleTranslations = useMemo(() => {
    if (showAllTranslations) {
      return cluster.translations;
    }

    const current = cluster.translations.find((item) => item.language === language);
    const fallback = cluster.translations.find((item) => item.language !== language);
    return [current, fallback].filter((item): item is NonNullable<typeof item> => Boolean(item));
  }, [cluster.translations, language, showAllTranslations]);

  const clusterImages = useMemo(() => {
    // Only include articles with usable image URLs; otherwise the API returns
    // 404 and the carousel renders broken slots.
    const articles = cluster.articles
      .filter((article) => article.hasImage)
      .map((article) => imageApiUrl("article", article.id));

    if (articles.length === 0 && cluster.hasImage) {
      return [
        imageApiUrl("cluster", cluster.id),
      ];
    }

    return articles;
  }, [cluster.articles, cluster.id, cluster.hasImage]);

  const clusterFallbackImage = imageApiUrl("cluster", cluster.id);

  const markArticleImageFailed = (articleId: string) => {
    setFailedArticleImageIds((current) => {
      const next = new Set(current);
      next.add(articleId);
      return next;
    });
  };

  return (
    <section className="space-y-6">
      <header className="rounded-3xl border border-border bg-card/60 p-6 shadow-xl shadow-black/20 sm:p-8">
        <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">duudey / {copy.nav.clusters}</p>
        <h1 className="mt-2 text-3xl font-black text-foreground sm:text-5xl">{translation?.title ?? cluster.id}</h1>
        <p className="mt-4 max-w-4xl text-sm text-foreground sm:text-base">{translation?.summary ?? copy.ui.noSummaryYet}</p>

        <div className="relative w-full aspect-video mt-6 rounded-lg border border-border overflow-hidden">
          {/* Keep a visual fallback when no cover exists or every slide fails. */}
          <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
          {mounted ? (
            <ClusterCarousel images={clusterImages} fallbackImage={clusterFallbackImage} />
          ) : clusterImages[0] ? (
            // SSR + pre-hydration placeholder: plain <Image> so the LCP
            // image is in initial HTML for crawlers and Core Web Vitals.
            <Image
              src={clusterImages[0]}
              alt=""
              fill
              className="object-cover"

            />
          ) : null}
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {cluster.bands.map((band) => (
            <BandBadge key={band.name} band={band} language={language} />
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          {cluster.translations.map((item) => (
            <Badge key={item.language} variant="subtle" className="uppercase tracking-[0.12em] text-foreground">
              {item.language}
            </Badge>
          ))}
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <Card className="border-border bg-card/50 col-span-full">
          <CardHeader>
            <CardTitle className="text-xl text-foreground">{copy.ui.articles}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {cluster.articles.map((article) => (
              <article key={article.id} className="rounded-2xl border border-border bg-muted p-4 flex">
                <div className="flex-1 p-1 max-w-[200px]">
                  {article.hasImage && !failedArticleImageIds.has(article.id) ? (
                    <Image
                      src={imageApiUrl("article", article.id)}
                      alt={article.title}
                      className="aspect-video w-full rounded object-cover"
                      width={320}
                      height={180}

                      onError={() => markArticleImageFailed(article.id)}
                    />
                  ) : (
                    <div className="flex aspect-video w-full items-center justify-center rounded border border-dashed border-border bg-muted text-[10px] uppercase tracking-[0.12em] text-muted-foreground max-w-[200px]">
                      {copy.ui.noImage}
                    </div>
                  )}
                </div>
                <div className="flex-1 p-2">
                  <p className="text-xs text-muted-foreground">{article.sourceName}</p>
                  <Link href={article.url} target="_blank" rel="noreferrer" className="mt-1 block text-sm font-semibold text-foreground transition hover:text-emerald-700 dark:hover:text-emerald-100">
                    {article.title}
                  </Link>
                  <p className="mt-2 text-xs text-muted-foreground">{formatDateTime(article.publishedAt, language)}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {article.tags.slice(0, 4).map((tag) => (
                      <Badge key={tag} variant="subtle" className="text-[11px] text-foreground">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </CardContent>
        </Card>

        <Card className="border-border bg-card/50">
          <CardHeader>
            <CardTitle className="text-xl text-foreground">{copy.ui.bandsInStory}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <article className="rounded-2xl border border-border bg-muted p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{copy.ui.featuredArtists}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {cluster.bands.map((band) => (
                  <BandBadge key={`${band.name}-${band.spotifyId ?? "none"}`} band={band} language={language} />
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">{copy.ui.spotifyHint}</p>
            </article>

            <article className="rounded-2xl border border-border bg-muted p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{copy.ui.coverageSnapshot}</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <p className="text-sm text-foreground">{copy.ui.articles}: <span className="font-semibold text-foreground">{cluster.articles.length}</span></p>
                <p className="text-sm text-foreground">{copy.ui.languages}: <span className="font-semibold text-foreground">{cluster.translations.length}</span></p>
                <p className="text-sm text-foreground">{copy.ui.bands}: <span className="font-semibold text-foreground">{cluster.bands.length}</span></p>
                <p className="text-sm text-foreground">{copy.ui.sources}: <span className="font-semibold text-foreground">{cluster.sourceNames.length}</span></p>
              </div>
            </article>
          </CardContent>
        </Card>

        <Card className="border-border bg-card/50">
          <CardHeader>
            <CardTitle className="text-xl text-foreground">{copy.ui.translations}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {visibleTranslations.map((item) => (
              <article key={item.language} className="rounded-2xl border border-border bg-muted p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-foreground">{item.title}</p>
                  <Badge variant="outline" className="border-border text-foreground">
                    {item.language.toUpperCase()}
                  </Badge>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{item.summary}</p>
              </article>
            ))}

            {cluster.translations.length > 2 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAllTranslations((current) => !current)}
                className="w-full rounded-xl border-border bg-muted text-foreground hover:bg-accent"
              >
                {showAllTranslations ? copy.ui.showFewerTranslations : `${copy.ui.showAllTranslations} (${cluster.translations.length})`}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      </div>



      <Button asChild variant="outline" className="rounded-2xl border-border bg-muted text-foreground hover:bg-accent">
        <Link href={withLanguagePrefix("/clusters", language)}>{copy.ui.backToClusters}</Link>
      </Button>
    </section>
  );
}
